import { getPartPhotoEntries } from '@/Client/Lib/scopeNavigatorPartPresentersLib';
import {
  getScanGroup,
  getScanTask,
  getScanWithUpdatedTask,
  getUniqueScanPhotos,
  type ScanGroupOptimized,
  type ScanPhoto,
  type ScanRootOptimized,
} from '@kaiizen/kai-scan-template-system';

import type { VoiceScopeCommand } from './voiceScope/voiceScopeTypes';

export type VideoToolboxProposalPhotoCandidate = {
  photoId: string;
};

export const VOICE_SCOPE_PROPOSAL_PHOTO_PICKER_LIMIT = 3;

const getPhotoSortTime = (photo: ScanPhoto): number => photo.addedAt ?? (photo.videoFrameTimeSeconds != null ? photo.videoFrameTimeSeconds * 1000 : 0);

/** The newest photos attached anywhere on the group (rollup), up to three. */
export const getRecentGroupPhotosForProposal = (
  group: ScanGroupOptimized,
  limit = VOICE_SCOPE_PROPOSAL_PHOTO_PICKER_LIMIT,
): ScanPhoto[] =>
  getPartPhotoEntries(group)
    .slice()
    .sort((left, right) => {
      const timeDelta = getPhotoSortTime(right) - getPhotoSortTime(left);
      if (timeDelta !== 0) return timeDelta;
      return right.photoId.localeCompare(left.photoId);
    })
    .slice(0, limit);

export const findGroupScanPhotoById = (
  group: ScanGroupOptimized,
  photoId: string,
): ScanPhoto | undefined => getPartPhotoEntries(group).find(photo => photo.photoId === photoId);

/**
 * Attach an existing group photo to every task touched by the voice proposal.
 * Preserves walkthrough provenance (`scanVideoId`, `videoFrameTimeSeconds`, legacy `orgVideoId`).
 */
export const attachPhotoToVoiceScopeTasksOnScan = (
  scanRoot: ScanRootOptimized,
  commands: VoiceScopeCommand[],
  photo: ScanPhoto,
): ScanRootOptimized => {
  const taskIds = [...new Set(commands.map(command => command.taskId))];
  let scan = scanRoot;

  for (const taskId of taskIds) {
    const command = commands.find(entry => entry.taskId === taskId);
    if (!command) continue;

    const { groupId } = command;
    const task = getScanTask(scan, groupId, taskId);
    scan = getScanWithUpdatedTask(scan, groupId, taskId, {
      photos: getUniqueScanPhotos(task.photos, [photo]),
    });
  }

  return scan;
};

export const attachPhotoToVoiceScopeTasks = (
  scanRoot: ScanRootOptimized,
  groupId: string,
  photoId: string,
  commands: VoiceScopeCommand[],
): ScanRootOptimized | undefined => {
  const group = getScanGroup(scanRoot, groupId);
  const photo = findGroupScanPhotoById(group, photoId);
  if (!photo) return undefined;
  return attachPhotoToVoiceScopeTasksOnScan(scanRoot, commands, photo);
};
